import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";

import comCSS from "./NumbersItem.module.scss";
import DOMPurify from 'isomorphic-dompurify';

const getInnerContent = (htmlString) => {
  const regex = /\{\{(.*?)\}\}/;
  const match = regex.exec(htmlString);

  if (match) {
    return { outer: match[0], inner: match[1] };
  } else {
    return null;
  }
};

const skipCurlyBracesInHtml = (html) => {
  const result = getInnerContent(html);
  if (!result) return html;

  try {
    let currentNumber = parseInt(result.inner);
    if (isNaN(currentNumber)) {
      return html;
    }

    return html.replace(result.outer, currentNumber);
  } catch (error) {
    console.error("Error parsing number:", error);
    return html;
  }
};

const modifyNumberInHtml = (
  html,
  setNumberHTML,
  finishedRef = {},
  animatingRef = {}
) => {
  const result = getInnerContent(html);
  if (!result) return html;

  try {
    let currentNumber = parseInt(result.inner);

    if (isNaN(currentNumber)) {
      return html;
    }
    const animationDuration = 2400;
    const delay = Math.round(animationDuration / currentNumber);

    function modifyNumberWithDelay(number) {
      if (Number(result.inner) <= number) {
        finishedRef.current = 1;
        animatingRef.current = false;
        return;
      }
      const newNumber = ++number;
      const modifiedHtml = html.replace(result.outer, newNumber);

      setTimeout(() => {
        // console.log(modifiedHtml); // Optional: Log the modified HTML
        animatingRef.current = true;
        setNumberHTML(modifiedHtml);
        modifyNumberWithDelay(newNumber); // Recursive call for next number
      }, delay);
    }

    // Start the modification process
    modifyNumberWithDelay(1);
  } catch (error) {
    console.error("Error parsing number:", error);
    return html;
  }
};

const NumbersItem = ({ className = "", data }) => {
  const { width: windowWidth } = useWindowDimensions();
  const [numberHTML, setNumberHTML] = useState(
    skipCurlyBracesInHtml(windowWidth < 992 ? data?.numberMobile : data?.number)
  );
  const wrapperRef = useRef(null);
  const finishedRef = useRef(0);
  const animatingRef = useRef(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const onScroll = () => {
        if (
          !wrapperRef?.current ||
          finishedRef?.current ||
          animatingRef?.current
        )
          return null;
        if (
          wrapperRef.current.getBoundingClientRect().top <
            window.innerHeight + 60 &&
          data
        ) {
          const number =
            window.innerWidth < 992 ? data.numberMobile : data.number;
          modifyNumberInHtml(
            number || "",
            setNumberHTML,
            finishedRef,
            animatingRef
          );
        }
      };

      window.addEventListener("scroll", onScroll, false);
      window.addEventListener("resize", onScroll, false);

      // Call handler right away so state gets updated with initial window size
      onScroll();

      return () => {
        window.removeEventListener("scroll", onScroll, false);
        window.removeEventListener("resize", onScroll, false);
      };
    }
  }, []);

  useEffect(() => {
    if (finishedRef?.current === 1 && data) {
      setNumberHTML(
        skipCurlyBracesInHtml(
          windowWidth < 992 ? data?.numberMobile : data?.number
        )
      );
    }
  }, [windowWidth, finishedRef, data]);

  const { textMobile } = data || {};

  let { text } = data || {};

  if (windowWidth < 992) {
    text = textMobile;
  }

  return (
    <div ref={wrapperRef} className={cn(className, comCSS.wrapper)}>
      <div className="number-wrap">
        <div
          className="large-number-anim"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.label) }}
        />
        <div className="text-color-slate-grey">{convertHTMLtoJSX(text)}</div>
      </div>
    </div>
  );
};

NumbersItem.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.string, // Unique identifier (consider making required)
    number: PropTypes.string, // Main numbered value
    numberMobile: PropTypes.string, // Mobile numbered value
    text: PropTypes.string, // Text content
    textMobile: PropTypes.string, // Mobile text content
  }),
};

export default NumbersItem;
